import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Flex, Input, Modal, Row, Table, TableProps, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
    importBusPassRecords,
    saveBusPassImportedRecords
} from "../../service/httpsCalls";
import { LoaderSpinner } from "../../components/common/Loader";
import "./billing.scss";
import { createTableConfig, ImportBusPassBaseColumns } from "./tableConfig";


function toCurrency(value: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
    }).format(value ?? 0);
}

export default function ImportBusPass({ invoiceInfo, parentCallback }) {
    const [tableHeight, setTableHeight] = React.useState(300);
    const [records, setRecords] = useState<any[]>([]);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const [file, setFile] = useState<any | null>(null);
    const [fileList, setFileList] = useState<any[]>([]);
    const [summary, setSummary] = useState({
        totalCount: 0,
        totalAmount: 0,
        totalBilledAmount: 0,
        uploadedFileName: "-"
    });
    const [hasInvalidRows, setHasInvalidRows] = useState(false);
    const selectedFileName = fileList?.length > 0 ? fileList[0]?.name : "";
    const hasData = (records?.length ?? 0) > 0;
    const disableImportSection = hasData;

    const columns = useMemo(() => {
       
        return createTableConfig(ImportBusPassBaseColumns);
    }, []);

    const tableChanges: TableProps<any>[ "onChange" ] = (_pagination, _filters, _sorter, extra) => {
        setFilteredRowCount(extra?.currentDataSource?.length ?? 0);
    };

   

    useEffect(() => {
        if (!records?.length) {
            setFilteredRowCount(0);
            return;
        }

        const removeResizeListener = updateTableHeight();
        return removeResizeListener;
    }, [records?.length]);

    const updateTableHeight = () => {
        const updateHeight = () => {
            const container = document.getElementById("buspass-table-container");
            if (container) {
                const header = container.getElementsByClassName("ant-table-header")[0]?.clientHeight ?? 10;
                setTableHeight(container.clientHeight - header);
            }
        };

        window.addEventListener("resize", updateHeight);
        updateHeight();

        return () => window.removeEventListener("resize", updateHeight);
    };
  

    function onUploadChange({ file: currentFile, fileList: list }) {
        if (currentFile?.status === "removed") {
            setFile(null);
            setFileList([]);
            return;
        }

        const uploadFileName = (currentFile?.name ?? "").toLowerCase();
        if (!uploadFileName.endsWith(".xlsx")) {
            message.error("Only .xlsx files are allowed.");
            setFile(null);
            setFileList([]);
            return Upload.LIST_IGNORE;
        }

        setFile(currentFile.originFileObj || currentFile);
        setFileList(list);
    }

    function importRecords() {
        if (!file) {
            parentCallback({
                isInvalid: true,
                errorMessage: "Please select a .xlsx file to import."
            });
            return;
        }

        setImporting(true);
        importBusPassRecords({
            clientId: invoiceInfo?.clientId,
            clientInvoiceSId: invoiceInfo?.id,
            file
        }, (res) => {
            setImporting(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error.messages[0].description : "Something went wrong!!!";
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                });
                return;
            }

            const items = res?.items ?? [];
            setRecords(items);
            setFilteredRowCount(0);
            setSummary({
                totalCount: Number(res?.totalRecords ?? items.length ?? 0),
                totalAmount: Number(res?.totalAmount ?? 0),
                totalBilledAmount: Number(res?.totalBilledAmount ?? 0),
                uploadedFileName: res?.uploadedFileName || "-"
            });
            setHasInvalidRows(Boolean(res?.hasInvalidRows ?? items.some((x: any) => x?.hasFormatError)));
            parentCallback({
                isInvalid: false,
                showMessage: true,
                message: res?.message ?? "Bus Pass records parsed successfully!"
            });
        });
    }

    function saveRecords() {
        if (!records?.length) {
            parentCallback({
                isInvalid: true,
                errorMessage: "No Bus Pass records available to save."
            });
            return;
        }

        if (hasInvalidRows) {
            parentCallback({
                isInvalid: true,
                errorMessage: "Rows highlighted in red contain invalid or missing values. Please correct data in proper format and re-import before save."
            });
            return;
        }

        if (!file) {
            parentCallback({
                isInvalid: true,
                errorMessage: "Please keep uploaded file selected before save."
            });
            return;
        }

        const isImportedStatus = (invoiceInfo?.status ?? "").toString().trim().toLowerCase() === "imported";
        const confirmMessage = isImportedStatus
            ? "Already imported data would get discarded. Do you want to continue?"
            : "Are you sure you want to save imported Bus Pass records?";

        Modal.confirm({
            title: confirmMessage,
            okText: "Yes",
            cancelText: "No",
            onOk() {
                setSaving(true);
                const saveRecordsPayload = (records ?? []).map((record: any) => ({
                    passNumber: record?.passNumber ?? "",
                    memberId: record?.memberId ?? "",
                    vendor: record?.vendor ?? "",
                    inventoryType: record?.inventoryType ?? "",
                    billingType: record?.billingType ?? "",
                    quantity: Number(record?.quantity ?? 0),
                    ticketCost: Number(record?.ticketCost ?? 0),
                    adminQty: Number(record?.adminQty ?? 0),
                    totalCharge: Number(record?.totalCharge ?? 0),
                    mailingDate: record?.mailingDate ?? null,
                    firstTripDate: record?.firstTripDate ?? null,
                    billable: record?.billable ?? "",
                    nonbillableDesc: record?.nonbillableDesc ?? "",
                    billingNote: record?.billingNote ?? ""
                }));
                saveBusPassImportedRecords({
                    clientId: invoiceInfo?.clientId,
                    clientInvoiceSId: invoiceInfo?.id,
                    records: saveRecordsPayload,
                    fileName: summary.uploadedFileName
                }, (res) => {
                    setSaving(false);
                    if (res?.hasError) {
                        const errorMsg = res?.error?.messages?.length > 0 ? res.error.messages[0].description : "Something went wrong!!!";
                        parentCallback({
                            isInvalid: true,
                            errorMessage: errorMsg
                        });
                        return;
                    }

                    setRecords([]);
                    setFilteredRowCount(0);
                    setSummary({
                        totalCount: 0,
                        totalAmount: 0,
                        totalBilledAmount: 0,
                        uploadedFileName: "-"
                    });
                    setHasInvalidRows(false);
                    setFile(null);
                    setFileList([]);
                    parentCallback({
                        isInvalid: false,
                        showMessage: true,
                        message: "Bus Pass records saved successfully!"
                    });
                });
            }
        });
    }

    function cancelImport() {
        Modal.confirm({
            title: "Do you want to reset imported Bus Pass data?",
            okText: "Yes",
            cancelText: "No",
            onOk() {
                setRecords([]);
                setFile(null);
                setFileList([]);
                setFilteredRowCount(0);
                setSummary({
                    totalCount: 0,
                    totalAmount: 0,
                    totalBilledAmount: 0,
                    uploadedFileName: "-"
                });
                setHasInvalidRows(false);
            }
        });
    }

    function resetSelectedFile() {
        setFile(null);
        setFileList([]);
    }
  
    return (
        <div style={{ width: "100%" }}>
            <div style={{ background: "#fff", padding: "15px" }}>
                <Row gutter={12}>
                    <Col span={24}>
                        <Flex align="center" gap="small">
                            <Upload
                                maxCount={1}
                                showUploadList={false}
                                fileList={fileList}
                                onChange={onUploadChange}
                                beforeUpload={() => false}
                                accept=".xlsx"
                                disabled={disableImportSection}
                            >
                                <Button disabled={disableImportSection}>
                                    <UploadOutlined /> Click to Upload
                                </Button>
                            </Upload>
                            <Input
                                allowClear
                                disabled={disableImportSection}
                                value={selectedFileName || ""}
                                placeholder="No file selected"
                                style={{ flex: 1, minWidth: "220px" }}
                                onKeyDown={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                onDrop={(e) => e.preventDefault()}
                                onChange={(e) => {
                                    if (!e?.target?.value) {
                                        resetSelectedFile();
                                    }
                                }}
                            />
                            <Button onClick={importRecords} loading={importing} type="primary" disabled={disableImportSection}>
                                Import
                            </Button>
                        </Flex>
                    </Col>
                </Row>
            </div>
            {
                hasData &&
                <>
                    <Card styles={{ body: { padding: "15px", paddingTop: "10px", height: "auto", maxHeight: "calc(100vh - 268px)" } }}>
                        <Flex justify="start" style={{ paddingBottom: "5px", paddingTop: "0", alignItems: "center", background: "#fff" }} gap="small">
                            <div style={{ background: "#e6f4ff", padding: "0px, 5px", fontWeight: 500 }}>
                                <span style={{ marginRight: "5px", marginLeft: "5px", borderRight: "1px solid #d4d2d2", paddingRight: "5px" }}>
                                    Total: {summary.totalCount}
                                </span>
                                <span style={{ marginRight: "5px", borderRight: "1px solid #d4d2d2", paddingRight: "5px" }}>
                                    Total Amount: {toCurrency(summary.totalAmount)}
                                </span>
                                <span style={{ marginRight: "5px", borderRight: "1px solid #d4d2d2", paddingRight: "5px" }}>
                                    Total Billed Amount: {toCurrency(summary.totalBilledAmount)}
                                </span>
                                <span style={{ marginRight: "5px" }}>
                                    File: {summary.uploadedFileName}
                                </span>
                                {
                                    filteredRowCount > 0 && filteredRowCount !== summary.totalCount &&
                                    <span style={{ marginRight: "5px", borderLeft: "1px solid #d4d2d2", paddingLeft: "5px" }}>
                                        Filtered: {filteredRowCount}
                                    </span>
                                }
                            </div>
                        </Flex>
                        <div id="buspass-table-container" style={{ height: "calc(100vh - 360px)", width: "100%", overflowY: "hidden", overflowX: "auto" }}>
                            <Table
                                className="bulkUpdateView buspass-grid"
                                dataSource={records}
                                columns={columns}
                                rowKey={(record: any, index: number) => record.sId ?? index}
                                size="small"
                                pagination={false}
                                tableLayout="auto"
                                onChange={tableChanges}
                                rowClassName={(record: any) => record?.hasFormatError ? "buspass-invalid-row" : ""}
                                scroll={{ x: 2400, y: tableHeight }}
                                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: recordsLoading }}
                            />
                        </div>
                    </Card>
                </>
            }
            {
                hasData &&
                <div style={{ marginTop: "12px", background: "#fff", padding: "15px" }}>
                    <Flex justify="end" gap="small">
                        <Button loading={saving} onClick={saveRecords} style={{ background: '#1677ff', color: '#fff' }}>
                            Save
                        </Button>
                        <Button onClick={cancelImport}>
                            Cancel
                        </Button>
                        
                    </Flex>
                </div>
            }
        </div>
    );
}
